import type { LootRefusalReason } from "odyssey-shared";
import { PENDING_TIMEOUT_MS, type BagTimer } from "../bag/store.ts";
import { applyAnswer, askable, markAsked, openChest, roomMade, settleLines, type Chest, type Gain } from "./lines.ts";
import type { ChestEvent } from "./wire.ts";

/**
 * The chest the page shows (P2.8-T16, ADR-091 decisions 19 and 22), owned by the game rather than by React, as the
 * bag's store is. It holds only what the server said: the window opens on a `loot` and never before, a line is taken
 * only when a `lootTaken` says so, and the page decides no take and invents no count (CONTRACTS 1.3, Category C);
 * the rules themselves are `lines.ts`'s. What this adds is the session around them:
 *
 * - **Which `loot` opens the window.** One for the chest the window already shows, or for the thing the page last
 *   asked to open (`expect`, told as E sends its `interact`). Any other is dropped: the map server sends a chest's
 *   lines again of its own accord after a take it could not make, and a window the player closed meanwhile stays
 *   closed. A `loot` that says `unavailable` opens nothing and is told as `cannotOpen`.
 * - **A take** is sent through the session's connection, which declines before the world is revealed and past the
 *   page's budget of item requests (`bag/requests.ts`); a take that was not sent marks nothing and is told as `busy`,
 *   so a press never does nothing in silence. The lines it names are pending until their answer, or until
 *   `PENDING_TIMEOUT_MS` passes with none, so a request lost on the way never leaves a row hatched for good: the wait's
 *   end is told as `unavailable`, and a late answer is still applied, once. A pending line is never asked for again.
 * - **The answer** is applied line by line; each refusal reason is told to the listeners (`onNotice`), which word it
 *   on the message line, and each line taken is told (`onGained`) for the loot toast. The answer to a take whose
 *   window has closed since (the player walked on) is still told, from the lines the window last showed, so what
 *   reached the bag is named; it opens nothing.
 * - **Closing.** The close button and Escape (`close`), the character dying (`dismiss`, since the dead take nothing
 *   and the death screen stands where the window would), the chest no longer reaching the character or leaving the
 *   view (`keepWhile`, asked each frame), a refusal that says the session no longer holds the chest, and the
 *   session's end (`reset`), after which the page knows nothing of any chest: a reconnect, a version swap and a
 *   second window of the same account each start from nothing, and the next E asks the server again.
 * - **Telling the server.** The chest stands open in the world while anyone has its window open (ADR-091's amendment
 *   of 2026-10-06), so the player's own close is told to the server (`bindClose`). No other close is: the server sees
 *   reach, death and the session's end for itself, and it sent the refusals.
 *
 * Listeners are told only on a change, and the snapshot is replaced on each change and kept otherwise, as
 * `useSyncExternalStore` needs.
 *
 * @example
 * ```ts
 * const chest = new ChestStore();
 * chest.bindTake((handle, lines) => connection.sendLootTake(handle, lines));
 * chest.expect(7);
 * chest.receive({ kind: "loot", handle: 7, result: "open", lines: [[0, "minor_healing_potion", 2, false]] });
 * chest.takeAll();
 * ```
 */

/**
 * What the store tells the page to say: that a chest's lines cannot be read now, or why a take was refused.
 */
export type ChestNotice = "cannotOpen" | LootRefusalReason;

/**
 * What a view of the chest reads: the chest shown, or null while the window is closed, and a way to hear it change.
 * Both work unbound, as React calls them.
 */
export interface ChestSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => Chest | null;
}

/**
 * Sends one `lootTake`: the chest's handle and the lines asked for.
 *
 * @returns Whether it was sent.
 */
export type TakeSender = (handle: number, lines: readonly number[]) => boolean;

/**
 * Tells the server that the player closed the loot window of a chest, by its handle.
 */
export type CloseSender = (handle: number) => void;

/**
 * The page's timer, on `setTimeout`.
 *
 * @param run What to run.
 * @param ms After how long.
 * @returns Cancels it.
 */
const PAGE_TIMER: BagTimer = (run, ms) => {
  const timer = setTimeout(run, ms);
  return () => clearTimeout(timer);
};

/**
 * The store.
 */
export class ChestStore implements ChestSource {
  private readonly listeners = new Set<() => void>();
  private readonly noticeListeners = new Set<(notice: ChestNotice) => void>();
  private readonly gainListeners = new Set<(gain: Gain) => void>();
  private chest: Chest | null = null;
  private left: Chest | null = null;
  private asked: number | null = null;
  private entry = false;
  private sender: TakeSender | null = null;
  private closer: CloseSender | null = null;
  private readonly waits = new Map<number, () => void>();

  /**
   * @param timer Schedules a pending mark's timeout; `setTimeout` on the page.
   */
  public constructor(private readonly timer: BagTimer = PAGE_TIMER) {}

  /**
   * Listens for changes.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the window reads, the same object until something changes.
   *
   * @returns The chest shown, or null while the window is closed.
   */
  public readonly getSnapshot = (): Chest | null => this.chest;

  /**
   * Sets what sends a `lootTake`: the session's connection.
   *
   * @param sender The sender.
   */
  public bindTake(sender: TakeSender): void {
    this.sender = sender;
  }

  /**
   * Sets what tells the server that the player closed a chest's window: the session's connection.
   *
   * @param closer The sender.
   */
  public bindClose(closer: CloseSender): void {
    this.closer = closer;
  }

  /**
   * Listens for what the page should say, each told after the store has taken the message behind it.
   *
   * @param listener Called with each notice.
   * @returns A function that stops listening.
   */
  public onNotice(listener: (notice: ChestNotice) => void): () => void {
    this.noticeListeners.add(listener);
    return () => {
      this.noticeListeners.delete(listener);
    };
  }

  /**
   * Listens for each line the server moved to the bag.
   *
   * @param listener Called with the item and the quantity of each line taken.
   * @returns A function that stops listening.
   */
  public onGained(listener: (gain: Gain) => void): () => void {
    this.gainListeners.add(listener);
    return () => {
      this.gainListeners.delete(listener);
    };
  }

  /**
   * Notes the thing the page has just asked the server to act on, so that the `loot` answering it opens the window.
   *
   * @param handle The thing's handle.
   */
  public expect(handle: number): void {
    this.asked = handle;
  }

  /**
   * Takes one chest message from the server.
   *
   * @param event The message.
   */
  public receive(event: ChestEvent): void {
    if (event.kind === "loot") {
      const shown = this.chest?.handle === event.handle;
      if (!shown && this.asked !== event.handle) {
        return;
      }
      this.asked = null;
      if (event.result !== "open") {
        if (shown) {
          this.shut();
        }
        this.tell("cannotOpen");
        return;
      }
      const next = openChest(event.handle, event.lines, this.chest);
      this.left = null;
      for (const line of [...this.waits.keys()]) {
        if (next.lines.find((each) => each.line === line)?.pending !== true) {
          this.endWait(line);
        }
      }
      this.change(next);
      return;
    }
    const held = this.chest?.handle === event.handle ? this.chest : this.left?.handle === event.handle ? this.left : null;
    if (held === null) {
      return;
    }
    const answer = applyAnswer(held, event.taken, event.refused);
    if (held !== this.chest) {
      this.left = answer.chest;
    } else if (answer.chest === null) {
      this.shut();
      this.left = null;
    } else {
      for (const each of answer.chest.lines) {
        if (!each.pending) {
          this.endWait(each.line);
        }
      }
      if (answer.chest !== this.chest) {
        this.change(answer.chest);
      }
    }
    for (const gain of answer.gained) {
      for (const listener of this.gainListeners) {
        listener(gain);
      }
    }
    for (const reason of answer.reasons) {
      this.tell(reason);
    }
  }

  /**
   * Hears that the bag changed: a line refused for want of room may be asked for again (ADR-091 decision 22).
   */
  public bagChanged(): void {
    if (this.chest === null) {
      return;
    }
    const next = roomMade(this.chest);
    if (next !== this.chest) {
      this.change(next);
    }
  }

  /**
   * Asks the server for one line.
   *
   * @param line The line.
   * @returns Whether the request was sent: false with no chest shown, for a line that is taken, waiting or refused
   *   for want of room, and when the connection declined, which is told as `busy`.
   */
  public take(line: number): boolean {
    return this.ask([line]);
  }

  /**
   * Asks the server for every line that may be asked for, in one request, so that Take all is one frame whatever the
   * chest holds.
   *
   * @returns Whether the request was sent.
   */
  public takeAll(): boolean {
    return this.ask(undefined);
  }

  /**
   * Closes the window, as its close button and Escape do, and tells the server which chest's window closed, so the
   * chest shuts once nobody has it open. The chest keeps its lines on the server, and the next open asks for them
   * again.
   */
  public close(): void {
    const chest = this.chest;
    if (chest !== null) {
      this.shut();
      this.closer?.(chest.handle);
    }
  }

  /**
   * Closes the window for a reason the server knows before the page could say it, so nothing is sent: the character
   * died.
   */
  public dismiss(): void {
    if (this.chest !== null) {
      this.shut();
    }
  }

  /**
   * Closes the window when the chest it shows no longer reaches the character or has left the view, and forgets a
   * thing asked to open that no longer does, so its late `loot` opens nothing.
   *
   * @param reaches Whether a thing, by handle, still reaches the character.
   */
  public keepWhile(reaches: (handle: number) => boolean): void {
    if (this.asked !== null && !reaches(this.asked)) {
      this.asked = null;
    }
    if (this.chest !== null && !reaches(this.chest.handle)) {
      this.shut();
    }
  }

  /**
   * Asks the open window to take the keyboard's focus, as Shift+E does (ADR-061's amendment "a play window never
   * stops play"); the window takes the wish with `takeEntry`. With no window open it does nothing.
   */
  public enter(): void {
    if (this.chest !== null) {
      this.entry = true;
      this.change({ ...this.chest });
    }
  }

  /**
   * Whether the window was asked to take the focus since it was last asked, answered once.
   *
   * @returns True when an entry waits.
   */
  public takeEntry(): boolean {
    const waiting = this.entry;
    this.entry = false;
    return waiting;
  }

  /**
   * Forgets everything as a session ends: the window closes, no wait runs on, and nothing asked is remembered.
   */
  public reset(): void {
    this.asked = null;
    if (this.chest !== null) {
      this.shut();
    }
    this.left = null;
  }

  /**
   * Sends a take for the lines wished for that may be asked for, and marks them pending when it was sent.
   *
   * @param wanted The lines wished for, or undefined for every line.
   * @returns Whether a request was sent.
   */
  private ask(wanted: readonly number[] | undefined): boolean {
    const chest = this.chest;
    if (chest === null || this.sender === null) {
      return false;
    }
    const lines = askable(chest, wanted);
    if (lines.length === 0) {
      return false;
    }
    if (!this.sender(chest.handle, lines)) {
      this.tell("busy");
      return false;
    }
    this.change(markAsked(chest, lines));
    for (const line of lines) {
      this.endWait(line);
      this.waits.set(
        line,
        this.timer(() => {
          this.waits.delete(line);
          if (this.chest !== null) {
            const next = settleLines(this.chest, [line]);
            if (next !== this.chest) {
              this.change(next);
              this.tell("unavailable");
            }
          }
        }, PENDING_TIMEOUT_MS),
      );
    }
    return true;
  }

  /**
   * Closes the window and ends every wait, keeping the lines it showed for an answer still on its way.
   */
  private shut(): void {
    for (const cancel of this.waits.values()) {
      cancel();
    }
    this.waits.clear();
    this.entry = false;
    this.left = this.chest;
    this.change(null);
  }

  /**
   * Cancels a line's timeout, if one runs.
   *
   * @param line The line.
   */
  private endWait(line: number): void {
    this.waits.get(line)?.();
    this.waits.delete(line);
  }

  /**
   * Tells the notice listeners.
   *
   * @param notice The notice.
   */
  private tell(notice: ChestNotice): void {
    for (const listener of this.noticeListeners) {
      listener(notice);
    }
  }

  /**
   * Replaces the snapshot and tells every listener.
   *
   * @param next The chest shown, or null.
   */
  private change(next: Chest | null): void {
    this.chest = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
